import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { api, ApiError } from "../api";
import type { AutoModeStatus, DeploymentBatch, ProjectSummary, TaskKind, TaskRecord, TrackedStatus } from "../types";
import { AutoTaskProgress } from "../components/AutoTaskProgress";
import { ChevronLeftIcon, MenuIcon, PlusIcon } from "../icons";
import { useIsNarrow } from "../viewport";
import { DEVELOPMENT_STATUSES, DEVELOPMENT_STATUS_LABEL, PRIORITY_LABEL, STATUSES, STATUS_LABEL, statusLabel, statusPill } from "./taskLabels";
import { NewTask, TaskView } from "./TaskDetail";

type KindFilter = "all" | TaskKind;

/** One choice of the state filter: which kind, states and archive it lists. */
interface StatusFilter {
	key: string;
	label: string;
	kind: TaskKind;
	status: TrackedStatus[];
	archived?: boolean;
}

const ORDINARY_FILTERS: StatusFilter[] = STATUSES.map((s) => ({ key: `ordinary:${s}`, label: STATUS_LABEL[s], kind: "ordinary", status: [s] }));
const DEVELOPMENT_FILTERS: StatusFilter[] = [
	...DEVELOPMENT_STATUSES.map((s): StatusFilter => ({ key: `development:${s}`, label: DEVELOPMENT_STATUS_LABEL[s], kind: "development", status: [s] })),
	{ key: "development:archived", label: "已归档", kind: "development", status: DEVELOPMENT_STATUSES, archived: true },
];

/** The list request for a kind and state filter: by default each kind's active states. */
function listQuery(kind: KindFilter, statusKey: string): Parameters<typeof api.tasks>[0] {
	const ofKind = kind === "all" ? undefined : kind;
	if (statusKey === "active") return { kind: ofKind };
	if (statusKey === "all") {
		const status: TrackedStatus[] = kind === "ordinary" ? STATUSES : kind === "development" ? DEVELOPMENT_STATUSES : [...new Set<TrackedStatus>([...STATUSES, ...DEVELOPMENT_STATUSES])];
		return { kind: ofKind, status };
	}
	const filter = [...ORDINARY_FILTERS, ...DEVELOPMENT_FILTERS].find((f) => f.key === statusKey);
	return filter ? { kind: filter.kind, status: filter.status, archived: filter.archived } : { kind: ofKind };
}

const SEARCH_DELAY_MS = 250;

function errorText(err: unknown, fallback: string): string {
	return err instanceof ApiError ? err.message : fallback;
}

const short = (sha: string) => sha.slice(0, 8);

type TasksProps = {
	projects: ProjectSummary[];
	onMenu: () => void;
	onOpenSession?: (id: string) => void;
	/** Opens on the deployment, as when sent from a loop whose change waits for it. */
	openDeploy?: boolean;
};

/**
 * A person's own tracked tasks: ordinary tasks they keep themselves and development tickets whose lifecycle their spec,
 * execution and deployment establish (ADR-0073). There is no delete: an ordinary task is closed, a ticket archived.
 * The list leads into auto mode and the deployment too; a wide screen shows what it opens beside it, a phone as a
 * page over it.
 */
export function Tasks(props: TasksProps) {
	const narrow = useIsNarrow();
	const list = useTaskList();
	return narrow ? <PhoneTasks {...props} list={list} /> : <WideTasks {...props} list={list} />;
}

type TaskList = ReturnType<typeof useTaskList>;

/** The owner's tasks under the chosen kind, state, project and search, with auto mode and the deployment, as both layouts read them. */
function useTaskList() {
	const [tasks, setTasks] = useState<TaskRecord[] | null>(null);
	const [kindFilter, setKindFilter] = useState<KindFilter>("all");
	const [statusFilter, setStatusFilter] = useState("active");
	const [projectFilter, setProjectFilter] = useState("");
	const [search, setSearch] = useState("");
	const [q, setQ] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [auto, setAuto] = useState<AutoModeStatus | null>(null);
	const [batch, setBatch] = useState<DeploymentBatch | null>(null);

	useEffect(() => {
		const timer = setTimeout(() => setQ(search.trim()), SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [search]);

	const refresh = useCallback(async () => {
		try {
			setTasks((await api.tasks({ ...listQuery(kindFilter, statusFilter), projectId: projectFilter || undefined, q: q || undefined })).tasks);
		} catch (err) {
			setError(errorText(err, "加载任务失败"));
		}
	}, [kindFilter, statusFilter, projectFilter, q]);

	const refreshAuto = useCallback(async () => {
		try {
			setAuto(await api.autoModeStatus());
		} catch {
			setAuto(null);
		}
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	useEffect(() => {
		void refreshAuto();
	}, [refreshAuto]);

	// How many changes wait for the deployment, for its row; the deployment reads the batch again when it opens.
	useEffect(() => {
		api.deploymentBatch().then(setBatch, () => setBatch(null));
	}, []);

	function chooseKind(kind: KindFilter) {
		setKindFilter(kind);
		// A state of the other kind no longer applies.
		if (statusFilter.includes(":") && kind !== "all" && !statusFilter.startsWith(`${kind}:`)) setStatusFilter("active");
	}

	/** A state of one kind lists that kind only. */
	function chooseStatus(key: string) {
		setStatusFilter(key);
		const kind = key.split(":")[0];
		if (kind === "ordinary" || kind === "development") setKindFilter(kind);
	}

	return { tasks, kindFilter, chooseKind, statusFilter, setStatusFilter, chooseStatus, projectFilter, setProjectFilter, search, setSearch, error, setError, auto, setAuto, batch, setBatch, refresh, refreshAuto };
}

/** What the list opens: beside it on a wide screen, as a page over it on a phone. */
type TaskPage = { page: "task"; task: TaskRecord } | { page: "new" } | { page: "auto" } | { page: "deploy" };

const PAGE_TITLE: Record<Exclude<TaskPage["page"], "task">, string> = { new: "新建任务", auto: "自动模式", deploy: "部署" };

/** How each layout moves between pages: opening one, replacing the open one, and closing it. */
interface Navigation {
	open: (page: TaskPage) => void;
	replace: (page: TaskPage) => void;
	close: () => void;
}

/** The wide layout: the list on the left, and beside it the task, auto mode or the deployment it opened; auto mode until one is chosen. */
function WideTasks(props: TasksProps & { list: TaskList }) {
	const { list } = props;
	const [page, setPage] = useState<TaskPage | null>(() => (props.openDeploy ? { page: "deploy" } : null));
	const [filtering, setFiltering] = useState(false);
	const shown: TaskPage | null = page ?? (list.auto ? { page: "auto" } : null);

	useEffect(() => {
		if (props.openDeploy) setPage({ page: "deploy" });
	}, [props.openDeploy]);

	const nav: Navigation = { open: setPage, replace: setPage, close: () => setPage(null) };

	return (
		<main className="chat tasks tasks-wide">
			<header className="chat-head page-head">
				<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={props.onMenu}>
					<MenuIcon size={22} />
				</button>
				<div className="titles">
					<span className="title">任务</span>
				</div>
				<button onClick={() => setPage({ page: "new" })}>＋ 新建任务</button>
			</header>
			<div className="tasks-body">
				<section className="task-side" aria-label="任务列表">
					<TaskListBody
						list={list}
						current={shown}
						onOpen={setPage}
						filters={<WideFilters list={list} projects={props.projects} open={filtering} onToggle={setFiltering} />}
					/>
				</section>
				<section className="task-pane">
					{shown && <TaskPageView page={shown} list={list} projects={props.projects} nav={nav} titled onOpenSession={props.onOpenSession} />}
				</section>
			</div>
		</main>
	);
}

/**
 * The phone layout, as a phone app pages: the list is a page of its own, and a task, a new task, auto mode and the
 * deployment each open as a page over it. Every page is a history entry, so the back button and the phone's back
 * gesture both go back one page; the list keeps its filters, rows and scroll underneath. Filters sit in a sheet.
 */
function PhoneTasks(props: TasksProps & { list: TaskList }) {
	const { list } = props;
	const [pages, setPages] = useState<TaskPage[]>(() => (props.openDeploy ? [{ page: "deploy" }] : []));
	const [sheet, setSheet] = useState(false);

	// Opened on the deployment, its page is a history entry over the list like any page opened here.
	useEffect(() => {
		if (props.openDeploy) history.pushState({ taskPages: 1 }, "");
	}, []);

	// Going back in history leaves as many pages as the entry it lands on records.
	useEffect(() => {
		const onPop = () => {
			const depth = (history.state as { taskPages?: unknown } | null)?.taskPages;
			setPages((open) => open.slice(0, typeof depth === "number" ? depth : 0));
		};
		addEventListener("popstate", onPop);
		return () => removeEventListener("popstate", onPop);
	}, []);

	function open(page: TaskPage) {
		const next = [...pages, page];
		history.pushState({ taskPages: next.length }, "");
		setPages(next);
	}

	function back() {
		if ((history.state as { taskPages?: unknown } | null)?.taskPages === pages.length) history.back();
		else setPages(pages.slice(0, -1));
	}

	/** The top page becomes `page`, as a saved new task becomes its own page. */
	function replace(page: TaskPage) {
		const next = [...pages.slice(0, -1), page];
		history.replaceState({ taskPages: next.length }, "");
		setPages(next);
	}

	const top = pages.at(-1);
	const title = !top ? "任务" : top.page === "task" ? fresh(list, top.task).title : PAGE_TITLE[top.page];

	return (
		<main className="chat tasks tasks-phone">
			<section className="phone-page" data-page aria-label="任务" hidden={top !== undefined}>
				<header className="phone-head">
					<button className="ghost sidebar-toggle icon-btn" aria-label="打开侧边栏" onClick={props.onMenu}>
						<MenuIcon size={22} />
					</button>
					<h1>任务</h1>
					<button className="phone-new" aria-label="新建任务" onClick={() => open({ page: "new" })}>
						<PlusIcon size={22} />
					</button>
				</header>
				<div className="phone-scroll">
					<TaskListBody
						list={list}
						current={null}
						chevrons
						onOpen={open}
						filters={
							<button className="task-filter" onClick={() => setSheet(true)}>
								筛选
							</button>
						}
					/>
				</div>
			</section>
			{top && (
				<section key={pages.length} className="phone-page phone-pushed" data-page aria-label={title}>
					<header className="phone-head">
						<button className="phone-back" aria-label="返回" onClick={back}>
							<ChevronLeftIcon size={26} />
						</button>
						<h1>{top.page === "task" ? "" : title}</h1>
						<span className="phone-head-side" />
					</header>
					<div className="phone-scroll">
						<TaskPageView page={top} list={list} projects={props.projects} nav={{ open, replace, close: back }} titled={false} onOpenSession={props.onOpenSession} />
					</div>
				</section>
			)}
			{sheet && <FilterSheet list={list} projects={props.projects} onClose={() => setSheet(false)} />}
		</main>
	);
}

/** The task as the list has it now, else as it was opened. */
const fresh = (list: TaskList, task: TaskRecord) => list.tasks?.find((t) => t.id === task.id) ?? task;

/** What a page shows: a task, a new task, auto mode or the deployment. */
function TaskPageView(props: { page: TaskPage; list: TaskList; projects: ProjectSummary[]; nav: Navigation; titled: boolean; onOpenSession?: (id: string) => void }) {
	const { page, list, nav } = props;
	const opened = (task: TaskRecord) => {
		nav.replace({ page: "task", task });
		void list.refresh();
	};
	if (page.page === "task") {
		const task = fresh(list, page.task);
		return (
			<TaskView
				key={task.id}
				task={task}
				projects={props.projects}
				progress={list.auto?.tasks.find((row) => row.id === task.id) ?? null}
				auto={list.auto ? list.auto.enabled : null}
				onSaved={opened}
				onOpenDeploy={() => nav.open({ page: "deploy" })}
				onOpenSession={props.onOpenSession}
				onAuto={list.setAuto}
			/>
		);
	}
	if (page.page === "new") return <NewTask key="new" projects={props.projects} titled={props.titled} onCancel={nav.close} onSaved={opened} />;
	if (page.page === "auto")
		return (
			<AutoModePanel
				status={list.auto}
				tasks={list.tasks}
				titled={props.titled}
				onStatus={list.setAuto}
				onRefresh={list.refreshAuto}
				onOpenTask={(task) => nav.open({ page: "task", task })}
			/>
		);
	return (
		<DeploymentPanel
			titled={props.titled}
			onBatch={list.setBatch}
			onApproved={() => {
				void list.refresh();
			}}
		/>
	);
}

const KIND_SEGMENTS: [KindFilter, string][] = [
	["all", "全部"],
	["ordinary", "普通"],
	["development", "开发"],
];

/** Auto mode's pickup in a word. */
const pickup = (auto: AutoModeStatus) => (auto.quota ? "额度已用尽" : auto.enabled ? "接手中" : auto.everEnabled ? "已暂停" : "未启用");

/** The list, as both layouts show it: rows into auto mode and the deployment, the kind, the filters and search, then the tasks. */
function TaskListBody(props: { list: TaskList; current: TaskPage | null; chevrons?: boolean; onOpen: (page: TaskPage) => void; filters: ReactNode }) {
	const { list, current } = props;
	const { auto, batch } = list;
	const chosen = [list.statusFilter !== "active" ? STATUS_CHOICES.find((c) => c.key === list.statusFilter)?.label : null, list.projectFilter ? "按项目" : null].filter(
		(part): part is string => Boolean(part),
	);
	const running = auto?.tasks.filter((row) => row.state === "running").length ?? 0;
	return (
		<>
			{list.error && (
				<p className="banner error">
					{list.error}{" "}
					<button className="ghost" onClick={() => list.setError(null)}>
						关闭
					</button>
				</p>
			)}
			<div className="task-links">
				{auto && (
					<button className={`task-link${current?.page === "auto" ? " current" : ""}`} onClick={() => props.onOpen({ page: "auto" })}>
						<span>自动模式</span>
						<span className="task-link-value">
							<i className={`task-dot${auto.enabled && !auto.quota ? " on" : ""}`} />
							{pickup(auto)}
							{running > 0 && ` · ${running} 个运行中`}
						</span>
						<span className="task-chev">›</span>
					</button>
				)}
				<button className={`task-link${current?.page === "deploy" ? " current" : ""}`} onClick={() => props.onOpen({ page: "deploy" })}>
					<span>部署</span>
					<span className={`task-link-value${batch && batch.ready.length > 0 ? " waiting" : ""}`}>
						{batch ? (batch.ready.length > 0 ? `${batch.ready.length} 个变更待部署` : "暂无待部署变更") : ""}
					</span>
					<span className="task-chev">›</span>
				</button>
			</div>
			<div className="task-toolbar">
				<div className="task-seg" role="group" aria-label="类型">
					{KIND_SEGMENTS.map(([kind, label]) => (
						<button key={kind} aria-pressed={list.kindFilter === kind} className={list.kindFilter === kind ? "on" : ""} onClick={() => list.chooseKind(kind)}>
							{label}
						</button>
					))}
				</div>
				{props.filters}
			</div>
			{chosen.length > 0 && (
				<p className="task-chosen">
					<span>{chosen.join(" · ")}</span>
					<button
						className="ghost"
						onClick={() => {
							list.setStatusFilter("active");
							list.setProjectFilter("");
						}}
					>
						清除
					</button>
				</p>
			)}
			<input type="search" value={list.search} placeholder="搜索任务" aria-label="搜索任务" onChange={(e) => list.setSearch(e.target.value)} />
			<div className="task-rows">
				{list.tasks && list.tasks.length === 0 && <p className="hint">无匹配任务</p>}
				{list.tasks?.map((t) => {
					const on = current?.page === "task" && current.task.id === t.id;
					return (
						<button key={t.id} className={`task-row${on ? " current" : ""}`} aria-current={on ? "true" : undefined} onClick={() => props.onOpen({ page: "task", task: t })}>
							<span className="task-row-text">
								<span className="task-row-title">{t.title}</span>
								<span className="task-row-meta">
									{t.kind === "development" && <span className="pill task-kind">开发</span>}
									<span>优先级{PRIORITY_LABEL[t.priority]}</span>
									{t.dueDate && <span>截止 {t.dueDate}</span>}
									{t.projectTitle && <span>{t.projectTitle}</span>}
									{t.archived && <span>已归档</span>}
								</span>
							</span>
							<span className={statusPill(t)}>{statusLabel(t)}</span>
							{props.chevrons && <span className="task-chev">›</span>}
						</button>
					);
				})}
			</div>
		</>
	);
}

/** Every choice of the state filter, grouped by kind. */
const STATUS_CHOICES: { key: string; label: string; group: string | null }[] = [
	{ key: "active", label: "进行中（默认）", group: null },
	{ key: "all", label: "全部状态", group: null },
	...ORDINARY_FILTERS.map((f) => ({ key: f.key, label: f.label, group: "普通任务" })),
	...DEVELOPMENT_FILTERS.map((f) => ({ key: f.key, label: f.label, group: "开发任务" })),
];

/** The filters: the state, grouped by kind, and the project. */
function FilterOptions(props: { list: TaskList; projects: ProjectSummary[] }) {
	const { list } = props;
	const groups: [string, typeof STATUS_CHOICES][] = [
		["状态", STATUS_CHOICES.filter((c) => c.group === null)],
		["普通任务", list.kindFilter === "development" ? [] : STATUS_CHOICES.filter((c) => c.group === "普通任务")],
		["开发任务", list.kindFilter === "ordinary" ? [] : STATUS_CHOICES.filter((c) => c.group === "开发任务")],
	];
	return (
		<>
			{groups
				.filter(([, choices]) => choices.length > 0)
				.map(([name, choices]) => (
					<div key={name} className="task-options" role="group" aria-label={name}>
						<h3>{name}</h3>
						<div>
							{choices.map((c) => (
								<button key={c.key} aria-pressed={list.statusFilter === c.key} className={list.statusFilter === c.key ? "on" : ""} onClick={() => list.chooseStatus(c.key)}>
									{c.label}
								</button>
							))}
						</div>
					</div>
				))}
			{props.projects.length > 0 && (
				<div className="task-options" role="group" aria-label="项目">
					<h3>项目</h3>
					<div>
						{[{ id: "", title: "所有项目" }, ...props.projects].map((p) => (
							<button key={p.id} aria-pressed={list.projectFilter === p.id} className={list.projectFilter === p.id ? "on" : ""} onClick={() => list.setProjectFilter(p.id)}>
								{p.title}
							</button>
						))}
					</div>
				</div>
			)}
		</>
	);
}

/** The wide screen's filters, in a popover under their button that a click elsewhere or Escape closes. */
function WideFilters(props: { list: TaskList; projects: ProjectSummary[]; open: boolean; onToggle: (open: boolean) => void }) {
	const { open, onToggle } = props;
	const box = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		const away = (e: PointerEvent) => {
			if (!box.current?.contains(e.target as Node)) onToggle(false);
		};
		const escape = (e: KeyboardEvent) => e.key === "Escape" && onToggle(false);
		document.addEventListener("pointerdown", away);
		document.addEventListener("keydown", escape);
		return () => {
			document.removeEventListener("pointerdown", away);
			document.removeEventListener("keydown", escape);
		};
	}, [open, onToggle]);

	return (
		<div className="task-filter-box" ref={box}>
			<button className={`task-filter${open ? " on" : ""}`} aria-expanded={open} onClick={() => onToggle(!open)}>
				筛选
			</button>
			{open && (
				<div className="task-popover" role="dialog" aria-label="筛选">
					<FilterOptions list={props.list} projects={props.projects} />
				</div>
			)}
		</div>
	);
}

/** The phone's filters, in a sheet over the list. */
function FilterSheet(props: { list: TaskList; projects: ProjectSummary[]; onClose: () => void }) {
	return (
		<>
			<div className="phone-scrim" onClick={props.onClose} />
			<section className="phone-sheet" role="dialog" aria-label="筛选">
				<div className="phone-grab" />
				<div className="phone-scroll">
					<FilterOptions list={props.list} projects={props.projects} />
					<button className="phone-done" onClick={props.onClose}>
						查看 {props.list.tasks?.length ?? 0} 个任务
					</button>
				</div>
			</section>
		</>
	);
}

/**
 * Auto mode's pickup and each ticket's auto progress (CONTEXT.md "Auto progress"). Hidden where the
 * server has no auto mode. The owner turns it on here, the first time too; pausing stops new pickup only.
 */
function AutoModePanel(props: {
	status: AutoModeStatus | null;
	tasks: TaskRecord[] | null;
	titled: boolean;
	onStatus: (status: AutoModeStatus) => void;
	onRefresh: () => Promise<void>;
	onOpenTask: (task: TaskRecord) => void;
}) {
	const { status } = props;
	// The limit as typed; null shows the current one.
	const [limit, setLimit] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/** Runs a control, which answers the fresh status; a refusal is shown and the status read again. */
	async function act(control: () => Promise<AutoModeStatus>): Promise<boolean> {
		setBusy(true);
		setError(null);
		try {
			props.onStatus(await control());
			return true;
		} catch (err) {
			setError(errorText(err, "操作失败"));
			await props.onRefresh();
			return false;
		} finally {
			setBusy(false);
		}
	}

	async function submitLimit(typed: string) {
		const value = Number(typed);
		if (!typed.trim() || !Number.isInteger(value) || value < 1) return setError("上限须为不小于 1 的整数");
		if (await act(() => api.setAutoModeLimit(value))) setLimit(null);
	}

	if (!status) return null;
	return (
		<section className="task-page" aria-label="自动模式">
			{props.titled && (
				<header className="task-doc-head">
					<h2>自动模式</h2>
				</header>
			)}
			{status.quota && <p className="banner">Codex 额度已用尽 · {new Date(status.quota.until).toLocaleString()} 恢复</p>}
			<div className="task-control">
				<div>
					<span className="task-grow">接手</span>
					<span className={`pill${status.enabled ? " tone-ok" : ""}`}>{status.enabled ? "接手中" : status.everEnabled ? "已暂停" : "未启用"}</span>
					{status.enabled ? (
						<button onClick={() => void act(() => api.pauseAutoMode())} disabled={busy}>
							暂停接手
						</button>
					) : (
						<button onClick={() => void act(() => api.resumeAutoMode())} disabled={busy}>
							开启自动模式
						</button>
					)}
				</div>
				<div>
					<label className="task-grow task-limit">
						<span>
							自动编码并发上限
						</span>
						<input type="number" min={1} step={1} value={limit ?? String(status.limit)} onChange={(e) => setLimit(e.target.value)} />
					</label>
					<button onClick={() => void submitLimit(limit ?? String(status.limit))} disabled={busy}>
						设置上限
					</button>
				</div>
			</div>
			{error && <p className="banner error">{error}</p>}
			{status.tasks.length > 0 && (
				<section className="task-sec">
					<h4>已接手任务 · {status.tasks.length}</h4>
					<div className="task-cards">
						{status.tasks.map((row) => {
							const task = props.tasks?.find((t) => t.id === row.id);
							const title = task?.title ?? row.change ?? row.id;
							return (
								<div key={row.id} className="task-card" role="group" aria-label={title}>
									<AutoTaskProgress
										row={row}
										title={
											task ? (
												<button className="task-card-title" onClick={() => props.onOpenTask(task)}>
													{title} ›
												</button>
											) : (
												<span className="task-card-title">{title}</span>
											)
										}
										onResume={() => void act(() => api.resumeAutoTask(row.id))}
									/>
								</div>
							);
						})}
					</div>
				</section>
			)}
		</section>
	);
}

/**
 * The shared server's deployment: it shows the complete ready batch, both people's changes, with the commit it
 * would pin as it opens; only an explicit act approves that reviewed batch, pinned at what was shown.
 */
function DeploymentPanel(props: { titled: boolean; onApproved: () => void; onBatch: (batch: DeploymentBatch) => void }) {
	const [batch, setBatch] = useState<DeploymentBatch | null>(null);
	const [approved, setApproved] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		// Once, as the page opens.
		void preview();
	}, []);

	async function preview() {
		setBusy(true);
		setError(null);
		setApproved(null);
		try {
			const read = await api.deploymentBatch();
			setBatch(read);
			props.onBatch(read);
		} catch (err) {
			setError(errorText(err, "读取待部署变更失败"));
		} finally {
			setBusy(false);
		}
	}

	async function approve(reviewed: DeploymentBatch) {
		if (!reviewed.newestSha) return;
		setBusy(true);
		setError(null);
		try {
			const answer = await api.approveDeployment(reviewed.newestSha);
			setApproved(answer.approved.sha);
			setBatch(null);
			props.onApproved();
		} catch (err) {
			setError(errorText(err, "批准部署失败"));
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="task-page" aria-label="部署">
			{props.titled && (
				<header className="task-doc-head">
					<h2>部署</h2>
				</header>
			)}
			{!batch && !approved && busy && <p className="hint">加载中…</p>}
			{batch && (
				<>
					<section className="task-sec">
						<h4>本批变更 · {batch.ready.length}</h4>
						{batch.ready.length === 0 ? (
							<p className="hint">暂无待部署变更</p>
						) : (
							<div className="task-batch">
								{batch.ready.map((change) => (
									<div key={change.id}>
										<span>{change.title}</span>
										<code>{change.change}</code>
										<code>{short(change.pushedSha)}</code>
									</div>
								))}
							</div>
						)}
					</section>
					{batch.elsewhere.length > 0 && (
						<section className="task-sec">
							<h4>同批其他提交 · {batch.elsewhere.length}</h4>
							<div className="task-batch">
								{batch.elsewhere.map((commit) => (
									<div key={commit.sha}>
										<span>{commit.subject}</span>
										<span className="hint">{commit.checkedBy ? `已在 ${commit.checkedBy} 的推送中检查` : "已单独检查"}</span>
										<code>{short(commit.sha)}</code>
									</div>
								))}
							</div>
						</section>
					)}
					{batch.blocked && <p className="banner error">无法批准：{batch.blocked}</p>}
				</>
			)}
			{approved && <p className="task-approved">已批准部署 {short(approved)} · 待部署结果</p>}
			{error && <p className="banner error">{error}</p>}
			<div className="row">
				{batch && (
					<button className="task-primary" onClick={() => void approve(batch)} disabled={busy || !batch.newestSha || batch.ready.length === 0 || batch.blocked !== null}>
						确认部署
					</button>
				)}
				<button className="ghost" onClick={() => void preview()} disabled={busy}>
					重新读取
				</button>
			</div>
		</section>
	);
}
